import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

import { AdminFeatureFlags } from '@reach/interfaces/user/admin-feature-flags.enum';
import { Language } from '@reach/interfaces/user/language.enum';
import {
	AdminDashboardFeatureFlags,
	UserFeatureFlags,
} from '@reach/interfaces/user/user-feature-flags';
import { UserInPing } from '@reach/interfaces/user/user-in-ping';

import { navigateToCurrentUrl } from '~/core/utils/navigate-to-current-url';

import { apiUserParser } from './parsers/api';
import { AuthApiService } from './session.api.service';
import { ClientSession } from './session.type';

@Injectable({ providedIn: 'root' })
export class SessionService {
	private readonly router = inject(Router);
	private readonly authApi = inject(AuthApiService);
	private readonly session$$ = new BehaviorSubject<ClientSession | null>(null);
	public readonly session$ = this.session$$.asObservable();
	public readonly $session = toSignal(this.session$);
	private sessionChecked = false;
	private _pinging = false;
	private _loggingIn = false;
	private _loggingOut = false;
	public readonly $sessionId = computed(
		() => {
			const session = this.$session();
			return session?.userId;
		},
		{ equal: (a, b) => a === b }
	);

	public get currentSession(): ClientSession | null {
		return this.session$$.getValue();
	}

	public updateSession(session: ClientSession | null): void {
		this.session$$.next(session);
	}

	public async ping(navigate = true): Promise<void> {
		if (this._pinging) {
			return;
		}

		try {
			this._pinging = true;
			const res = await this.authApi.ping().catch(() => null);
			this.setUser(res);

			if (navigate) {
				this.redirectToSameRoute();
			}
		} catch (error) {
			console.error(error);
			throw error;
		} finally {
			this._pinging = false;
		}
	}

	private setUser(user: UserInPing | null): void {
		this.session$$.next(apiUserParser(user));
	}

	public async getAdminRole(): Promise<AdminFeatureFlags | null> {
		return await this.doAndLoadUserIfNecessary(async () => {
			return this.session$$.value?.adminRole ?? null;
		});
	}

	public hasPermission(permission: AdminDashboardFeatureFlags): boolean {
		const user = this.session$$.getValue();
		const isAdmin = user?.adminRole === AdminFeatureFlags.SUPER_ADMIN;
		const hasFlag = user?.adminDashboardFlags.includes(permission);

		return isAdmin || hasFlag || false;
	}

	public async hasPermissionAndLoadUser(
		permission: AdminDashboardFeatureFlags
	): Promise<boolean> {
		return await this.doAndLoadUserIfNecessary(async () => {
			return this.hasPermission(permission);
		});
	}

	public async canAccessPage(): Promise<boolean> {
		const permissionsToCheck = Object.values(AdminDashboardFeatureFlags);
		let hasPermission = false;
		for (const permission of permissionsToCheck) {
			hasPermission = await this.hasPermissionAndLoadUser(permission);
			if (hasPermission) {
				break;
			}
		}
		return hasPermission;
	}

	private async doAndLoadUserIfNecessary<T>(fn: () => Promise<T>): Promise<T> {
		if (!this.sessionChecked) {
			const res = await this.authApi.ping().catch(() => null);
			this.setUser(res);
			this.sessionChecked = true;
		}

		return await fn();
	}

	public async login(username: string, password: string): Promise<void> {
		if (this._loggingIn) {
			return;
		}

		try {
			this._loggingIn = true;
			const session = await this.authApi.login(username, password);
			this.setUser(session);
			this.sessionChecked = true;
			this.redirectToSameRoute();
		} finally {
			this._loggingIn = false;
		}
	}

	public async logout(): Promise<void> {
		if (this._loggingOut) {
			return;
		}

		try {
			this._loggingOut = true;
			await this.authApi.logout();
			this.session$$.next(null);
			this.redirectToAuth();
		} catch (error) {
			console.error(error);
			this.session$$.next(null);
		} finally {
			this._loggingOut = false;
		}
	}

	public hasFeatureFlag(flag: UserFeatureFlags): boolean {
		return this.session$$.value?.featureFlags.includes(flag) ?? false;
	}

	public async redirectToSameRoute(): Promise<boolean> {
		return await navigateToCurrentUrl(this.router);
	}

	private async redirectToAuth(): Promise<boolean> {
		return await this.router.navigate(['/'], { onSameUrlNavigation: 'reload' });
	}

	public async microsoftSignIn(accessToken: string, language: Language): Promise<void> {
		try {
			await this.socialSignInHandler(async () => {
				const res = await this.authApi.signInWithMicrosoft(accessToken, language);
				this.setUser(res);
				return res;
			});
		} catch (error) {
			console.error(error);
		}
	}

	private async socialSignInHandler(socialLogin: () => Promise<unknown>): Promise<void> {
		try {
			await socialLogin();

			try {
				await this.ping();
			} catch (_) {}
		} catch (error) {
			this.session$$.next(null);
			console.error(error);
		}
	}
}
